import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { toast } from "sonner"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/modules/shared/ui/components/dialog"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import { Button } from "@/modules/shared/ui/components/button"
import { Checkbox } from "@/modules/shared/ui/components/checkbox"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/modules/shared/ui/components/table"
import type { EmployeeDto } from "@/modules/onboarding/employee/dtos"
import { onboardingService } from "@/modules/onboarding/infrastructure/onboarding-service"
import { apiClient } from "@/lib/api-client"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import type { RootState } from "@/store"

interface ExportRow {
  employeeId: string
  firstName: string
  lastName: string
  employeeCode: string
  isLearner: boolean
  selected: boolean
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: EmployeeDto[]
}

export function ExportToSageVipDialog({
  open,
  onOpenChange,
  employees,
}: Props) {
  const organizationId = useSelector((s: RootState) =>
    selectCurrentOrganizationId(s),
  )
  const [rows, setRows] = useState<ExportRow[]>([])
  const [vipCompanyCode, setVipCompanyCode] = useState("")
  const [payFrequency, setPayFrequency] = useState<
    "Monthly" | "Fortnightly" | "Weekly"
  >("Monthly")
  const [fileName, setFileName] = useState("SageVIP_Export")
  const [isFetching, setIsFetching] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const selectedRows = rows.filter((r) => r.selected)
  const allSelected = rows.length > 0 && rows.every((r) => r.selected)
  const someSelected = rows.some((r) => r.selected)

  useEffect(() => {
    if (!open || !organizationId) return

    setRows(
      employees.map((e) => ({
        employeeId: e.id,
        firstName: e.firstName,
        lastName: e.lastName,
        employeeCode: "",
        isLearner: false,
        selected: true,
      })),
    )

    setIsFetching(true)
    apiClient
      .get(`/organizations/${organizationId}/sagevip/connection`)
      .then((res) => {
        setVipCompanyCode(res.data?.companyCode ?? "")
      })
      .catch(() => {
        setVipCompanyCode("")
      })
      .finally(() => setIsFetching(false))
  }, [open, organizationId, employees])

  const updateRow = (employeeId: string, patch: Partial<ExportRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.employeeId === employeeId ? { ...r, ...patch } : r)),
    )
  }

  const toggleSelectAll = () => {
    setRows((prev) => prev.map((r) => ({ ...r, selected: !allSelected })))
  }

  const handleExport = async () => {
    if (!organizationId) {
      toast.error("No organisation selected")
      return
    }
    if (!vipCompanyCode.trim()) {
      toast.error("VIP Company Code is required")
      return
    }
    if (!fileName.trim()) {
      toast.error("File name is required")
      return
    }
    if (selectedRows.length === 0) {
      toast.error("Select at least one employee to export")
      return
    }
    const invalidCode = selectedRows.find(
      (r) => !/^[a-zA-Z0-9]{1,8}$/.test(r.employeeCode.trim()),
    )
    if (invalidCode) {
      toast.error(
        `Employee code for ${invalidCode.firstName} ${invalidCode.lastName} must be 1–8 alphanumeric characters, no spaces`,
      )
      return
    }

    setIsExporting(true)
    try {
      await onboardingService.exportToSageVip(
        organizationId,
        vipCompanyCode.trim(),
        fileName.trim(),
        selectedRows.map((r) => ({
          employeeId: r.employeeId,
          employeeCode: r.employeeCode.trim(),
          isLearner: r.isLearner,
          payFrequency,
        })),
      )
      toast.success("CSV downloaded successfully")
      onOpenChange(false)
    } catch {
      toast.error("Failed to export CSV")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Export to Sage VIP</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Enter an employee code for each employee before exporting.
          </p>
        </DialogHeader>

        {isFetching ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="size-6" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col gap-1">
              <Label>VIP Company Code</Label>
              <Input
                value={vipCompanyCode}
                onChange={(e) => setVipCompanyCode(e.target.value)}
                placeholder="e.g. 001"
                className="max-w-xs"
              />
              <p className="text-xs text-muted-foreground">
                Pre-filled from your Sage VIP connection. Update on the
                Integrations page if incorrect.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <Label>Export File Name</Label>
              <div className="flex items-center max-w-xs">
                <Input
                  value={fileName}
                  onChange={(e) =>
                    setFileName(e.target.value.replace(/\s/g, ""))
                  }
                  placeholder="e.g. SageVIP_Export"
                  className="rounded-r-none"
                />
                <span className="px-3 py-2 text-sm text-muted-foreground border border-l-0 rounded-r-md bg-muted select-none">
                  .csv
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <Label>Pay Frequency</Label>
              <Select
                value={payFrequency}
                onValueChange={(v) =>
                  setPayFrequency(v as "Monthly" | "Fortnightly" | "Weekly")
                }
              >
                <SelectTrigger className="max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="Monthly" className="hover:bg-slate-200">
                    Monthly
                  </SelectItem>
                  <SelectItem
                    value="Fortnightly"
                    className="hover:bg-slate-200"
                  >
                    Fortnightly
                  </SelectItem>
                  <SelectItem value="Weekly" className="hover:bg-slate-200">
                    Weekly
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Used to calculate hours per period in the export.
              </p>
            </div>

            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {selectedRows.length} of {rows.length} employees selected
              </span>
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-blue-600 hover:underline text-xs"
              >
                {allSelected ? "Deselect all" : "Select all"}
              </button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allSelected}
                        data-state={
                          !allSelected && someSelected
                            ? "indeterminate"
                            : undefined
                        }
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Employee Code *</TableHead>
                    <TableHead className="text-center">Learner</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.employeeId}
                      className={!row.selected ? "opacity-50" : ""}
                    >
                      <TableCell>
                        <Checkbox
                          checked={row.selected}
                          onCheckedChange={(checked) =>
                            updateRow(row.employeeId, {
                              selected: checked === true,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="text-foreground">
                        {row.firstName} {row.lastName}
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.employeeCode}
                          onChange={(e) =>
                            updateRow(row.employeeId, {
                              employeeCode: e.target.value,
                            })
                          }
                          placeholder="e.g. EMP001"
                          className="h-8"
                          disabled={!row.selected}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={row.isLearner}
                          disabled={!row.selected}
                          onCheckedChange={(checked) =>
                            updateRow(row.employeeId, {
                              isLearner: checked === true,
                            })
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
          <span className="shrink-0 mt-0.5">⚠️</span>
          <p>
            Before importing, please verify:{" "}
            <strong>banking details, tax number and ID numbers</strong>.
            Tempered Talent is not responsible for incorrect data imported into
            Sage VIP.
          </p>
        </div>

        <DialogFooter className="pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700 text-white border-0"
            onClick={handleExport}
            disabled={isFetching || isExporting || selectedRows.length === 0}
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Exporting…
              </>
            ) : (
              "Export CSV"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
